import { type SessionV2, types as dqCoreTypes } from "@decentriq/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { loadAsync } from "jszip";
import { useCallback, useEffect, useMemo } from "react";
import { useApiCore } from "contexts";
import { logDebug, parseMediaDataRoomError } from "utils";
import {
  type MediaDataRoomJobHookResult,
  type MediaDataRoomJobHookResultCacheSetter,
  type MediaDataRoomJobStatus,
} from "../models";
import {
  type MediaDataRoomComputeRequestKey,
  type MediaDataRoomJobHookPayload,
} from "./models";

/**
 *This hook is used to create request, run it if not skipped and fetch the results of the job for using job ID and compute node name from request response. It also caches the job in API platform database.
 *
 * @param input `MediaDataRoomJobInput` includes essential info for sending request, like request key, dataRoomId, etc, please check `MediaDataRoomJobInput` for more details
 * @param requestCreator function which needs to return body of the request for the given key
 * @param session optional `SessionV2`, if not provided, it will be fetched
 * @param skip if true, the job result fetching will be skipped
 * @param transform function to transform the job result, it provides the zip file in `JSZip` format
 * @returns object with the job result, error, loading state, retry function and status
 */
const useMediaDataRoomJob = <T, K extends MediaDataRoomComputeRequestKey>({
  input,
  requestCreator,
  session,
  skip,
  transform,
}: MediaDataRoomJobHookPayload<T, K>): MediaDataRoomJobHookResult<T> => {
  const queryClient = useQueryClient();
  const { client, getSession } = useApiCore();
  const handleGettingSession = useCallback(
    async (session?: SessionV2) => session ?? (await getSession()),
    [getSession]
  );
  // Fetch existing job
  const { data: existingJob, isLoading: isCheckingForExitingJob } = useQuery({
    enabled: !skip,
    queryFn: async () => {
      if (input.skipCheckForExistingJob) {
        return null;
      }
      try {
        const action = await client.getDataRoomActionInfo(input.dataRoomId, {
          [input.key]: requestCreator(),
          kind: "media",
        });
        if (
          action.jobs.length &&
          (await client.getJobFreshness(action.jobs[0]))
        ) {
          logDebug("||| Reuse job for ", input.key);
          return { id: action.jobs[0] };
        }
      } catch (error) {
        logDebug(
          `Unable to get fresh job or job was not created yet for ${input.key.toString()}: `,
          error
        );
        return null;
      }
      return null;
    },
    queryKey: input.withExistingJobSubKey().buildQueryKey(),
    retry: false,
  });
  // Create job
  const {
    mutate: createJobMutation,
    error: createJobError,
    isPending: isCreatingJob,
    reset: resetCreateJob,
  } = useMutation({
    mutationFn: async () => {
      return await client.createJob(input.dataRoomId, {
        [input.key]: requestCreator(),
        kind: "media",
      });
    },
    onSuccess: (data) => {
      logDebug("||| New job created for ", input.key, data.id);
      queryClient.setQueryData(
        input.withExistingJobSubKey().buildQueryKey(),
        data
      );
    },
  });
  // Polling for job status
  const {
    data: jobStatus,
    isLoading: isCheckingJobStatus,
    error: jobStatusError,
  } = useQuery({
    enabled: Boolean(existingJob) && !skip,
    queryFn: async () => {
      if (!existingJob) return null;
      try {
        const status = await client.getJobStatus(existingJob.id);
        const isCompleted = status.completed_at !== null;
        return status.status === dqCoreTypes.JobStatus.FAILED
          ? "FAILED"
          : isCompleted
            ? "COMPLETED"
            : "COMPUTING";
      } catch (error) {
        logDebug("||| Failed to get job status", existingJob.id, input.key);
        throw parseMediaDataRoomError(error);
      }
    },
    queryKey: input.withJobStatusSubKey().withJob(existingJob).buildQueryKey(),
    refetchInterval: (query) => {
      if (existingJob && query.state.data === "COMPUTING") {
        return input.timeout;
      }
    },
    retry: false,
  });
  const setCacheData = useCallback<MediaDataRoomJobHookResultCacheSetter<T>>(
    (dataOrSet) => {
      // TODO: Check with product if existing job still fresh if we can rely on local cache from it or not
      const key = input
        .withFetchResultsSubKey()
        .withJob(existingJob)
        .buildQueryKey();
      queryClient.setQueryData<T | undefined>(key, (oldData) =>
        typeof dataOrSet === "function"
          ? (dataOrSet as (prev: T | undefined) => T | undefined)(oldData)
          : dataOrSet
      );
    },
    // Its safe to rely only on `input.queryChangingKey` being changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, input.queryChangingKey, existingJob]
  );
  const downloadJobResult = useCallback(
    async (
      session: SessionV2,
      jobId: string,
      resultHash: string
    ): Promise<Uint8Array> => {
      const [manifestHashHex, encryptionKey] =
        await session.sendRetrieveResultEncryptionKeyRequest(jobId, resultHash);
      const stream = await client.downloadJobResult(
        jobId,
        manifestHashHex,
        resultHash,
        encryptionKey
      );
      return await client.readStream(stream);
    },
    [client]
  );
  // Query the results or error
  const {
    data: computeResults,
    isLoading: isFetchingJobResults,
    error: resultsFetchingError,
  } = useQuery({
    enabled: (jobStatus === "COMPLETED" || jobStatus === "FAILED") && !skip,
    queryFn: async () => {
      logDebug("||| Querying results for job", existingJob?.id, input.key);
      try {
        if (!existingJob) {
          throw new Error("Compute job not found");
        }
        const jobTasks = await client.getJobTasks(existingJob.id);
        const effectiveSession = await handleGettingSession(session);
        if (jobStatus === "FAILED") {
          const errorResults = [];
          for (const task of Object.values(jobTasks.tasks)) {
            for (const [selector, resultHash] of Object.entries(task.results)) {
              if (
                selector.startsWith("error") &&
                task.completed.includes(selector)
              ) {
                const error = await downloadJobResult(
                  effectiveSession,
                  existingJob.id,
                  resultHash
                );
                errorResults.push([task.name, new TextDecoder().decode(error)]);
              }
            }
          }
          throw new Error(
            `Job failed with errors: ${errorResults.map(([name, error]) => `${name}: ${error}`).join("\n")}`
          );
        }
        const jobTaskResultHash =
          jobTasks.tasks[jobTasks.target_task].results["success"];
        if (jobTaskResultHash === null) {
          throw new Error("Did not find job task result");
        }
        const resultStream = await downloadJobResult(
          effectiveSession,
          existingJob.id,
          jobTaskResultHash
        );
        const zip = await loadAsync(resultStream);
        return await transform(zip);
      } catch (error) {
        throw parseMediaDataRoomError(error);
      }
    },
    queryKey: input
      .withFetchResultsSubKey()
      .withJob(existingJob)
      .buildQueryKey(),
    retry: false,
  });
  useEffect(() => {
    if (!skip && existingJob == null && !isCheckingForExitingJob) {
      createJobMutation();
    }
  }, [createJobMutation, existingJob, isCheckingForExitingJob, skip]);
  const retry = useCallback(async () => {
    if (skip || !session) {
      throw new Error("Cannot retry disabled query");
    }
    resetCreateJob();
    return Promise.resolve(createJobMutation());
  }, [skip, createJobMutation, session, resetCreateJob]);
  const error = useMemo(
    () => createJobError ?? jobStatusError ?? resultsFetchingError ?? undefined,
    [createJobError, jobStatusError, resultsFetchingError]
  );
  const hasError = useMemo(() => Boolean(error), [error]);
  const hasJobOrFailedCreating = useMemo(
    () => Boolean(existingJob) || Boolean(createJobError),
    [existingJob, createJobError]
  );
  const status = useMemo<MediaDataRoomJobStatus>(
    () =>
      skip
        ? "IDLE"
        : isCheckingForExitingJob ||
            isCreatingJob ||
            isCheckingJobStatus ||
            jobStatus === "COMPUTING" ||
            !hasJobOrFailedCreating
          ? "COMPUTING"
          : isFetchingJobResults
            ? "FETCHING"
            : hasError
              ? "ERROR"
              : "COMPLETED",
    [
      skip,
      isCheckingForExitingJob,
      isCreatingJob,
      isCheckingJobStatus,
      jobStatus,
      isFetchingJobResults,
      hasError,
      hasJobOrFailedCreating,
    ]
  );
  return useMemo(
    () => ({
      computeResults,
      error,
      loading: (["COMPUTING", "FETCHING"] as MediaDataRoomJobStatus[]).includes(
        status
      ),
      retry,
      setCacheData,
      status,
    }),
    [computeResults, error, status, retry, setCacheData]
  );
};

export default useMediaDataRoomJob;
